import { toaster } from 'baseui/toast';
import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FinalButton } from '../../Components/FinalButton';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { Text } from '../../globalStyles';
import {
  SaiSurveyTerm as Term,
  useCreateSaiSurveyMutation,
  useGetSaiSurveysForOrgQuery,
  useUpdateSaiSurveyMutation,
} from '../../graphQL';
import { SurveyPeriodDisplay } from './components/SurveyPeriodDisplay';
import { SurveyPeriodForm } from './components/SurveyPeriodForm';
import { calculateYears } from './components/surveyUtils';

type SurveyPeriodFormType = {
  id?: number;
  startDate: string;
  endDate: string;
  surveyName: string;
  term: Term;
  surveyContentVersion: string;
  calendarYear: string;
  academicYearStart: string;
  academicYearEnd: string;
};

const defaultSurveyPeriodValues: SurveyPeriodFormType = {
  startDate: '',
  endDate: '',
  surveyName: '',
  term: Term.Fall,
  surveyContentVersion: '',
  calendarYear: '',
  academicYearStart: '',
  academicYearEnd: '',
};

interface SurveyPeriodProps {
  organizationId: number;
}

// Main Survey Period Config component
export function SurveyPeriod({ organizationId }: SurveyPeriodProps): JSX.Element {
  // Replace separate booleans with a single mode state
  const [mode, setMode] = useState<'idle' | 'creating' | 'editing'>('idle');
  const [selectedSurveyPeriodId, setSelectedSurveyPeriodId] = useState<number | null>(null);
  const [stateError, setStateError] = useState<string>('');

  // Create separate form instances for create and edit
  const createForm = useForm<SurveyPeriodFormType>({
    defaultValues: defaultSurveyPeriodValues,
    mode: 'onChange',
  });

  const editForm = useForm<SurveyPeriodFormType>({
    defaultValues: defaultSurveyPeriodValues,
    mode: 'onChange',
  });

  // Use the appropriate form based on mode
  const currentForm = mode === 'editing' ? editForm : createForm;

  // Fetch all configs for this org
  const {
    data: allOrgSurveyPeriodsData,
    loading: allOrgSurveyPeriodsLoading,
    error: allOrgSurveyPeriodsError,
    refetch: refetchAllOrgSurveyPeriods,
  } = useGetSaiSurveysForOrgQuery({
    variables: { organizationId },
    notifyOnNetworkStatusChange: true,
  });

  const [createSurveyPeriod, createMutation] = useCreateSaiSurveyMutation({
    onCompleted: ({ createSaiSurvey }) => {
      const responseType = createSaiSurvey.__typename;

      if (responseType === 'CreateSaiSurveyError') {
        setStateError(createSaiSurvey.message ?? 'Error creating survey period.');
        return;
      }

      if (responseType === 'CreateSaiSurveySuccess') {
        setMode('idle');
        setSelectedSurveyPeriodId(null);
        createForm.reset(defaultSurveyPeriodValues);
        toaster.positive('Survey period created successfully', { autoHideDuration: 3000 });
        refetchAllOrgSurveyPeriods();
        return;
      }

      setStateError(`CreateSaiSurvey: Invalid typename: ${responseType}`);
    },
    onError: error => {
      setStateError(error.message);
    },
  });
  const [updateSurveyPeriod, updateMutation] = useUpdateSaiSurveyMutation({
    onCompleted: ({ updateSaiSurvey }) => {
      const responseType = updateSaiSurvey.__typename;

      if (responseType === 'UpdateSaiSurveyError') {
        setStateError(updateSaiSurvey.message ?? 'Error updating survey config.');
        return;
      }

      if (responseType === 'UpdateSaiSurveySuccess') {
        handleCancel();
        toaster.positive('Survey config updated successfully', { autoHideDuration: 3000 });
        refetchAllOrgSurveyPeriods();
        return;
      }

      setStateError(`UpdateSaiSurvey: Invalid typename: ${responseType}`);
    },
    onError: error => {
      setStateError(error.message);
    },
  });

  // Handler to enter edit mode and hydrate form
  const handleEdit = useCallback(
    surveyPeriod => {
      setStateError('');
      setSelectedSurveyPeriodId(surveyPeriod.id);
      editForm.reset({
        id: surveyPeriod.id,
        startDate: surveyPeriod.startDate,
        endDate: surveyPeriod.endDate,
        surveyName: surveyPeriod.name,
        term: surveyPeriod.term,
        surveyContentVersion: surveyPeriod.assessmentKey,
        calendarYear: String(surveyPeriod.calendarYear ?? ''),
        academicYearStart: String(surveyPeriod.academicYearStart ?? ''),
        academicYearEnd: String(surveyPeriod.academicYearEnd ?? ''),
      });
      setMode('editing');
    },
    [editForm]
  );
  const startDate = currentForm.watch('startDate');
  const term = currentForm.watch('term');

  // Calculate calendar year and academic year
  const { calendarYear, academicYearStart, academicYearEnd } = useMemo(
    () => calculateYears(startDate, term),
    [startDate, term]
  );

  const academicYearDisplay = useMemo(
    () => (academicYearStart && academicYearEnd ? `${academicYearStart}-${academicYearEnd}` : ''),
    [academicYearStart, academicYearEnd]
  );

  // Memoize handlers to prevent unnecessary re-renders
  const handleCreate = useCallback(
    async (values: SurveyPeriodFormType) => {
      setStateError('');
      await createSurveyPeriod({
        variables: {
          data: {
            startDate: values.startDate,
            endDate: values.endDate,
            organizationId: organizationId,
            name: values.surveyName,
            term: values.term,
            assessmentKey: values.surveyContentVersion,
          },
        },
      });
    },
    [createSurveyPeriod, organizationId]
  );

  const handleCancel = useCallback(() => {
    setMode('idle');
    setSelectedSurveyPeriodId(null);
    setStateError('');
    createForm.reset(defaultSurveyPeriodValues);
    editForm.reset(defaultSurveyPeriodValues);
  }, [createForm, editForm]);

  const handleUpdate = useCallback(
    async (values: SurveyPeriodFormType) => {
      if (!selectedSurveyPeriodId) return;
      setStateError('');
      await updateSurveyPeriod({
        variables: {
          updates: {
            id: selectedSurveyPeriodId,
            startDate: values.startDate,
            endDate: values.endDate,
            name: values.surveyName,
            term: values.term,
            assessmentKey: values.surveyContentVersion,
          },
        },
      });
    },
    [selectedSurveyPeriodId, updateSurveyPeriod]
  );

  const handleStartCreate = useCallback(() => {
    createForm.reset(defaultSurveyPeriodValues);
    setSelectedSurveyPeriodId(null);
    setStateError('');
    setMode('creating');
  }, [createForm]);

  // Derive UI state
  const isEditing = mode === 'editing';
  const isCreating = mode === 'creating';

  const submitHandler = useMemo(
    () => currentForm.handleSubmit(isEditing ? handleUpdate : handleCreate),
    [currentForm, isEditing, handleUpdate, handleCreate]
  );

  // Display all configs
  const renderAllConfigs = () => {
    if (allOrgSurveyPeriodsLoading) return <LoadingPage />;
    if (allOrgSurveyPeriodsError)
      return <Text.bodySmall kind="danger">Error loading configs</Text.bodySmall>;
    if (!allOrgSurveyPeriodsData?.getSaiSurveysForOrg?.length)
      return (
        <EmptyState>
          <Text.bodySmall>No configs found.</Text.bodySmall>
        </EmptyState>
      );
    return (
      <ConfigsWrapper>
        <ConfigsHeader>All Survey Period Configs</ConfigsHeader>
        {allOrgSurveyPeriodsData.getSaiSurveysForOrg.map(surveyPeriod => {
          const isEditingThis = isEditing && selectedSurveyPeriodId === surveyPeriod.id;
          return (
            <ConfigCard key={surveyPeriod.id}>
              {isEditingThis ? (
                <FormProvider {...editForm}>
                  <SurveyPeriodForm
                    isEditing={true}
                    onSubmit={submitHandler}
                    onCancel={handleCancel}
                    createLoading={createMutation.loading}
                    updateLoading={updateMutation.loading}
                    errorMessage={stateError}
                    calendarYear={calendarYear}
                    academicYear={academicYearDisplay}
                  />
                </FormProvider>
              ) : (
                <>
                  <SurveyPeriodDisplay surveyPeriod={surveyPeriod} />
                  <EditButton
                    kind="outline_black"
                    size="compact"
                    onClick={() => handleEdit(surveyPeriod)}
                  >
                    Edit
                  </EditButton>
                </>
              )}
            </ConfigCard>
          );
        })}
      </ConfigsWrapper>
    );
  };

  return (
    <SectionRoot>
      <Text.h3>Survey Period Configuration</Text.h3>
      <IntroCopy>
        Configure survey periods to control when surveys are active for students.
      </IntroCopy>
      {renderAllConfigs()}
      {isCreating && (
        <CreateContainer>
          <CreateTitle>Create New Survey Period Config</CreateTitle>
          <FormProvider {...createForm}>
            <SurveyPeriodForm
              isEditing={false}
              onSubmit={submitHandler}
              onCancel={handleCancel}
              createLoading={createMutation.loading}
              updateLoading={updateMutation.loading}
              errorMessage={stateError}
              calendarYear={calendarYear}
              academicYear={academicYearDisplay}
            />
          </FormProvider>
        </CreateContainer>
      )}
      {mode === 'idle' && (
        <div>
          <FinalButton
            kind="primary"
            onClick={handleStartCreate}
            disabled={isEditing || !!allOrgSurveyPeriodsData?.getSaiSurveysForOrg?.length}
          >
            Create New Survey Period Config
          </FinalButton>
          {!!allOrgSurveyPeriodsData?.getSaiSurveysForOrg?.length && (
            <SingleConfigNotice>
              Only one survey period config is allowed at this time. You already have a survey
              period config. Click "Edit" to modify it.
            </SingleConfigNotice>
          )}
        </div>
      )}
    </SectionRoot>
  );
}

// Styled components
const SectionRoot = styled.div`
  margin-top: 1.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e0e0e0;
`;

const IntroCopy = styled(Text.bodySmall)`
  margin: 0.5rem 0 1rem 0;
  display: block;
`;

const ConfigsWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const ConfigsHeader = styled(Text.bodyBold)`
  margin: 0 0 0.5rem 0;
  display: block;
`;

const ConfigCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: white;
`;

const CreateContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  padding: 1.25rem;
  background: #f8f9fa;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
`;

const CreateTitle = styled(Text.bodyBold)`
  margin: 0 0 0.5rem 0;
  display: block;
`;

const SingleConfigNotice = styled(Text.bodySmall)`
  display: block;
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  margin-bottom: 4rem;
`;

const EditButton = styled(FinalButton)`
  margin-top: 0.5rem;
`;
